<script setup lang="ts">
import EntityCardShell from '@/components/entity/EntityCardShell.vue';
import EntityActionButton from '@/components/entity/EntityActionButton.vue';
import { Button } from '@/components/ui/button';
import { ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from '@/components/ui/context-menu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { formatMoney } from '~/lib/formatters';
import type { Invoice } from '~/types/models';
import { BriefcaseBusiness, Building2, Check, CheckCircle2, CircleDollarSign, Copy, Download, Eye, FileText, LoaderCircle, Mail, MoreHorizontal, Pencil, Trash2 } from 'lucide-vue-next';
import { toast } from 'vue-sonner';

const props = withDefaults(defineProps<{
    invoice: Invoice;
    showProject?: boolean;
    showClient?: boolean;
    canMarkPaid?: boolean;
    canView?: boolean;
    canEdit?: boolean;
    canSend?: boolean;
    canDelete?: boolean;
    canCopyPublicLink?: boolean;
    projectName?: string;
    clientName?: string;
    quoteNumber?: string;
    quoteHref?: string;
    surface?: 'default' | 'embedded' | 'list';
}>(), {
    showProject: true,
    showClient: true,
    canMarkPaid: false,
    canView: true,
    canEdit: false,
    canSend: false,
    canDelete: false,
    canCopyPublicLink: false,
    projectName: '',
    clientName: '',
    quoteNumber: '',
    quoteHref: '',
    surface: 'default',
});

const emit = defineEmits<{
    markPaid: [invoiceId: string];
    view: [invoice: Invoice];
    edit: [invoice: Invoice];
    send: [invoiceId: string];
    remind: [invoiceId: string];
    'copy-public-link': [invoice: Invoice];
    delete: [invoiceId: string];
    details: [invoice: Invoice];
}>();

function lifecycleBadgeLabel(invoice: Invoice): string {
    if (invoice.status === 'paid') return 'Settled';
    if (invoice.status === 'partial') return 'Partially paid';
    if (invoice.status === 'overdue') return 'Needs follow-up';
    if (invoice.status === 'sent') return 'Awaiting payment';
    return 'Draft';
}

function paidAmount(invoice: Invoice): number {
    if (invoice.status === 'paid') return Number(invoice.amountTotal || 0);
    return Number(invoice.amountPaid || 0);
}

function outstandingAmount(invoice: Invoice): number {
    return Math.max(0, Number(invoice.amountDue || 0));
}

function primaryActionLabel(invoice: Invoice): string {
    if (invoice.status === 'draft') return 'Send to client';
    if (invoice.status !== 'paid') return 'Mark paid';
    return 'View invoice';
}

function triggerPrimaryAction(invoice: Invoice) {
    if (invoice.status === 'draft' && props.canSend) {
        emit('send', invoice.id);
        return;
    }

    if (invoice.status !== 'paid' && props.canMarkPaid) {
        emit('markPaid', invoice.id);
        return;
    }

    emit('view', invoice);
}

const invoiceInitials = computed(() => {
    const source = String(props.invoice.title || props.invoice.invoiceNumber || 'Invoice').trim();
    return source
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase() || 'IV';
});

const subtitle = computed(() => {
    const parts = [
        props.invoice.invoiceNumber || 'No invoice number',
        props.invoice.issueDate ? `Issued ${props.invoice.issueDate}` : '',
        props.invoice.dueDate ? `Due ${props.invoice.dueDate}` : '',
    ].filter(Boolean);
    return parts.join(' • ');
});

const fallbackClass = 'bg-muted [&>span]:text-muted-foreground';
const { downloadInvoicePdf, isDownloadingInvoice } = useInvoicePdfDownload();

async function downloadPdf() {
    try {
        await downloadInvoicePdf(props.invoice);
        toast.success('Invoice PDF downloaded');
    } catch (error: any) {
        toast.error(error?.data?.statusMessage || error?.message || 'Invoice PDF could not be downloaded.');
    }
}
</script>

<template>
    <EntityCardShell
        :title="invoice.title || invoice.invoiceNumber || 'Invoice'"
        :subtitle="subtitle"
        :activate-on-click="canView"
        :fallback-text="invoiceInitials"
        :fallback-class="fallbackClass"
        :surface="surface"
        @activate="emit('details', invoice)"
    >
        <template #mobile-actions>
            <Button
                v-if="canView"
                size="sm"
                variant="outline"
                @click="emit('details', invoice)"
            >
                <Eye class="mr-2 size-4" />
                View details
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action" aria-label="More invoice actions">
                        <MoreHorizontal class="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem :disabled="isDownloadingInvoice(invoice.id)" @click="downloadPdf">
                        <LoaderCircle v-if="isDownloadingInvoice(invoice.id)" class="mr-2 size-4 animate-spin" />
                        <Download v-else class="mr-2 size-4" />
                        Download PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canView" @click="emit('view', invoice)">
                        <Eye class="mr-2 size-4" />
                        View invoice
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canCopyPublicLink" @click="emit('copy-public-link', invoice)">
                        <Copy class="mr-2 size-4" />
                        Copy public link
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canEdit" @click="emit('edit', invoice)">
                        <Pencil class="mr-2 size-4" />
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && invoice.status === 'draft'" @click="emit('send', invoice.id)">
                        <Mail class="mr-2 size-4" />
                        Send to client
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && invoice.status !== 'draft' && invoice.status !== 'paid'" @click="emit('remind', invoice.id)">
                        <Mail class="mr-2 size-4" />
                        Send reminder
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canMarkPaid && invoice.status !== 'paid'" @click="emit('markPaid', invoice.id)">
                        <Check class="mr-2 size-4" />
                        Mark paid
                    </DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canDelete" />
                    <DropdownMenuItem v-if="canDelete" class="text-destructive focus:text-destructive" @click="emit('delete', invoice.id)">
                        <Trash2 class="mr-2 size-4" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #actions>
            <EntityActionButton
                v-if="canView"
                label="View invoice details"
                :icon="Eye"
                @click="emit('details', invoice)"
            />
            <EntityActionButton
                v-if="(invoice.status === 'draft' && canSend) || (invoice.status !== 'paid' && canMarkPaid)"
                :label="primaryActionLabel(invoice)"
                :icon="invoice.status === 'draft' ? Mail : Check"
                @click="triggerPrimaryAction(invoice)"
            />
            <DropdownMenu>
                <DropdownMenuTrigger as-child>
                    <Button variant="ghost" size="icon" class="entity-row-action" aria-label="More invoice actions">
                        <MoreHorizontal class="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" class="w-56">
                    <DropdownMenuItem :disabled="isDownloadingInvoice(invoice.id)" @click="downloadPdf">
                        <LoaderCircle v-if="isDownloadingInvoice(invoice.id)" class="mr-2 size-4 animate-spin" />
                        <Download v-else class="mr-2 size-4" />
                        Download PDF
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canView" @click="emit('view', invoice)">
                        <Eye class="mr-2 size-4" />
                        View invoice
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canCopyPublicLink" @click="emit('copy-public-link', invoice)">
                        <Copy class="mr-2 size-4" />
                        Copy public link
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canEdit" @click="emit('edit', invoice)">
                        <Pencil class="mr-2 size-4" />
                        Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && invoice.status === 'draft'" @click="emit('send', invoice.id)">
                        <Mail class="mr-2 size-4" />
                        Send to client
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canSend && invoice.status !== 'draft' && invoice.status !== 'paid'" @click="emit('remind', invoice.id)">
                        <Mail class="mr-2 size-4" />
                        Send reminder
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="canMarkPaid && invoice.status !== 'paid'" @click="emit('markPaid', invoice.id)">
                        <Check class="mr-2 size-4" />
                        Mark paid
                    </DropdownMenuItem>
                    <DropdownMenuSeparator v-if="canDelete" />
                    <DropdownMenuItem v-if="canDelete" class="text-destructive focus:text-destructive" @click="emit('delete', invoice.id)">
                        <Trash2 class="mr-2 size-4" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </template>

        <template #body>
            <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div class="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                    <span class="inline-flex items-center gap-1.5 capitalize">
                        <span
                            class="size-2 rounded-full"
                            :class="invoice.status === 'paid' ? 'bg-emerald-500' : invoice.status === 'overdue' ? 'bg-destructive' : 'bg-muted-foreground/50'"
                        />
                        {{ invoice.status }} · {{ lifecycleBadgeLabel(invoice) }}
                    </span>
                    <span v-if="showClient" class="inline-flex items-center gap-1.5">
                        <Building2 class="size-3.5" />
                        {{ clientName }}
                    </span>
                    <span v-if="showProject && invoice.projectId" class="inline-flex items-center gap-1.5">
                        <BriefcaseBusiness class="size-3.5" />
                        {{ projectName || invoice.projectId }}
                    </span>
                    <span v-if="invoice.quoteId" class="inline-flex items-center gap-1.5">
                        <FileText class="size-3.5" />
                        {{ quoteNumber || invoice.quoteId }}
                    </span>
                    <span class="inline-flex items-center gap-1.5" title="Paid amount">
                        <CheckCircle2 class="size-3.5" />
                        {{ formatMoney(paidAmount(invoice), invoice.currency || 'USD') }}
                    </span>
                    <span class="inline-flex items-center gap-1.5" title="Outstanding amount">
                        <CircleDollarSign class="size-3.5" />
                        {{ formatMoney(outstandingAmount(invoice), invoice.currency || 'USD') }}
                    </span>
                </div>

                <p class="shrink-0 text-right text-lg font-semibold text-foreground">
                    {{ formatMoney(invoice.amountTotal, invoice.currency || 'USD') }}
                </p>
            </div>
        </template>

        <template #context-menu>
            <ContextMenuContent class="w-56">
                <ContextMenuItem :disabled="isDownloadingInvoice(invoice.id)" @click="downloadPdf">
                    <LoaderCircle v-if="isDownloadingInvoice(invoice.id)" class="mr-2 size-4 animate-spin" />
                    <Download v-else class="mr-2 size-4" />
                    Download PDF
                </ContextMenuItem>
                <ContextMenuItem v-if="canView" @click="emit('view', invoice)">
                    <Eye class="mr-2 size-4" />
                    View invoice
                </ContextMenuItem>
                <ContextMenuItem v-if="canEdit" @click="emit('edit', invoice)">
                    <Pencil class="mr-2 size-4" />
                    Edit
                </ContextMenuItem>
                <ContextMenuItem v-if="canSend && invoice.status === 'draft'" @click="emit('send', invoice.id)">
                    <Mail class="mr-2 size-4" />
                    Send to client
                </ContextMenuItem>
                <ContextMenuItem v-if="canSend && invoice.status !== 'draft' && invoice.status !== 'paid'" @click="emit('remind', invoice.id)">
                    <Mail class="mr-2 size-4" />
                    Send reminder
                </ContextMenuItem>
                <ContextMenuItem v-if="canMarkPaid && invoice.status !== 'paid'" @click="emit('markPaid', invoice.id)">
                    <Check class="mr-2 size-4" />
                    Mark paid
                </ContextMenuItem>
                <ContextMenuSeparator v-if="canDelete" />
                <ContextMenuItem v-if="canDelete" class="text-destructive focus:text-destructive" @click="emit('delete', invoice.id)">
                    <Trash2 class="mr-2 size-4" />
                    Delete
                </ContextMenuItem>
            </ContextMenuContent>
        </template>
    </EntityCardShell>
</template>
